<script lang="ts" setup>
type Props = {
    /**
     * Identifiant de l'input
     */
    inputId: string
    /**
     * Le préfixe de l'input
     */
    prefix?: string
    /**
     * Le suffixe de l'input
     */
    suffix?: string
    /**
     * Le label de l'input
     */
    label?: string
    /**
     * Message d'aide
     */
    hint?: string
    /**
     * Message d'erreur
     */
    error?: string

    /**
     * Si le label doit être flottant
     */
    unfloat?: boolean

    /**
     * Orientation du label
     */
    orientation?: "horizontal" | "vertical"
}

withDefaults(defineProps<Props>(), {
    prefix: "",
    suffix: "",
    label: "",
    hint: "",
    error: "",
    unfloat: false,
    orientation: "vertical",
})
</script>

<template>
    <div
        class="base-input-wrapper"
        :class="{
            'flex flex-col': unfloat,
            'flex-row-reverse justify-end items-center h-full':
                orientation === 'horizontal',
        }"
    >
        <slot name="header"></slot>
        <label
            :for="inputId"
            class="text-gray-500 text-sm z-10"
            :class="{
                'mb-2': orientation !== 'horizontal',
                'ml-2 pb-1': orientation === 'horizontal',
            }"
            v-if="unfloat && (label || $slots.label)"
            ><span v-html="label"></span>
        </label>
        <BaseInputGroup
            :class="[
                orientation === 'horizontal' ? 'w-auto!' : 'w-full',
                'min-w-0',
            ]"
        >
            <slot name="prefix"></slot>
            <BaseInputGroupAddon
                v-if="prefix"
                :pt="{ root: { class: 'shrink-0' } }"
            >
                <span class="whitespace-nowrap">{{ prefix }}</span>
            </BaseInputGroupAddon>
            <!-- Pas de wrapper autour du FloatLabel : les sélecteurs PrimeVue InputGroup ciblent > .p-floatlabel (:first / :last-child). -->
            <BaseFloatLabel
                v-if="!unfloat"
                variant="on"
                :pt="{
                    root: {
                        class: 'min-w-0 flex-1 w-full',
                    },
                }"
            >
                <label :for="inputId" class="text-gray-500 text-sm z-10">
                    {{ label }} <slot name="label"></slot>
                </label>
                <slot></slot>
            </BaseFloatLabel>
            <!-- Mode unfloat : wrapper flex ; les rayons sont répliqués en :deep (le champ n’est plus enfant direct du groupe). -->
            <div
                v-else
                class="base-input-wrapper__middle min-w-0 flex-1 w-full"
            >
                <slot></slot>
            </div>
            <BaseInputGroupAddon
                v-if="suffix"
                :pt="{ root: { class: 'shrink-0' } }"
            >
                <span class="whitespace-nowrap">{{ suffix }}</span>
            </BaseInputGroupAddon>
            <slot name="suffix"></slot>
        </BaseInputGroup>
        <BaseMessage
            v-if="hint || $slots.hint"
            severity="secondary"
            variant="simple"
            size="small"
            class="mt-1"
        >
            <p
                v-if="$slots.hint"
                class="mb-0 flex flex-wrap items-center gap-x-1 gap-y-0.5"
            >
                <slot name="hint"></slot>
            </p>
            <p v-else v-html="hint"></p>
        </BaseMessage>
        <BaseMessage
            v-if="error"
            severity="error"
            variant="simple"
            size="small"
            :pt="{
                content: 'border-none!',
            }"
        >
            <p v-html="error"></p>
        </BaseMessage>
    </div>
</template>

<style scoped>
/**
 * Même logique que @primeuix/styles/inputgroup pour un champ enveloppé (mode unfloat)
 * : le groupe cible `> .p-floatlabel` / `> .p-component`, pas un div intermédiaire.
 */
.base-input-wrapper :deep(.p-inputgroup > .base-input-wrapper__middle) {
    display: flex;
    flex: 1 1 0%;
    min-width: 0;
    width: 100%;
    align-items: stretch;
}

.base-input-wrapper
    :deep(.p-inputgroup > .base-input-wrapper__middle .p-inputtext),
.base-input-wrapper
    :deep(.p-inputgroup > .base-input-wrapper__middle .p-inputnumber-input) {
    flex: 1 1 auto;
    width: 1%;
    margin: 0;
    border-radius: 0;
}

.base-input-wrapper
    :deep(.p-inputgroup > .base-input-wrapper__middle:first-child .p-inputtext),
.base-input-wrapper
    :deep(
        .p-inputgroup
            > .base-input-wrapper__middle:first-child
            .p-inputnumber-input
    ) {
    border-start-start-radius: var(
        --p-form-field-border-radius,
        var(--p-border-radius-md)
    );
    border-end-start-radius: var(
        --p-form-field-border-radius,
        var(--p-border-radius-md)
    );
}

.base-input-wrapper
    :deep(.p-inputgroup > .base-input-wrapper__middle:last-child .p-inputtext),
.base-input-wrapper
    :deep(
        .p-inputgroup
            > .base-input-wrapper__middle:last-child
            .p-inputnumber-input
    ) {
    border-start-end-radius: var(
        --p-form-field-border-radius,
        var(--p-border-radius-md)
    );
    border-end-end-radius: var(
        --p-form-field-border-radius,
        var(--p-border-radius-md)
    );
}
</style>
